import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { listarUsuarios } from '../../usuarios/api';
import { ActionModal } from '../../oportunidades/components/ActionModal';
import { useVersoesDaBase } from '../hooks';
import {
  useAprovarBaseComercial,
  useCancelarBaseComercial,
  usePublicarBaseComercial,
  useRejeitarBaseComercial,
  useSolicitarAprovacao,
  useSubstituirBaseComercial,
  useSuspenderBaseComercial,
} from '../hooks';
/** Mínimo que os modais precisam — a fila de aprovação passa apenas o
    item da listagem, não a Base completa. */
export interface BaseComercialRef {
  id: string;
  codigo: string;
  versao: number;
  nome: string;
}

interface BaseProps {
  base: BaseComercialRef;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Modais das ações de ciclo de vida da Base Comercial (task 11.1).
 *
 * Cada ação mapeia um endpoint por evento de negócio (design §D6) — a
 * situação nunca é enviada diretamente. Justificativas obrigatórias
 * onde a RN exige (rejeição, suspensão, cancelamento, substituição).
 */
export const SolicitarAprovacaoModal: React.FC<BaseProps> = ({ base, isOpen, onClose, onSuccess }) => {
  const [err, setErr] = useState<string | null>(null);
  const mut = useSolicitarAprovacao(base.id);

  const submit = async () => {
    setErr(null);
    try {
      await mut.mutateAsync();
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao solicitar aprovação.'));
    }
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Solicitar aprovação"
      description="Encaminha a Base para aprovação do Gerente Comercial. Não é possível solicitar sem ao menos um item, nem se a cópia exigir revisão das condições (RN-COM-027)."
      submitLabel="Solicitar aprovação"
      isSubmitting={mut.isPending}
      submitError={err}
    >
      <p className="text-sm text-gray-600">
        <strong>{base.codigo} v{base.versao}</strong> — {base.nome}. Os aprovadores
        habilitados (perfil Gerente Comercial) serão notificados.
      </p>
    </ActionModal>
  );
};

export const AprovarBaseModal: React.FC<BaseProps> = ({ base, isOpen, onClose, onSuccess }) => {
  const [justificativa, setJustificativa] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const mut = useAprovarBaseComercial(base.id);

  const submit = async () => {
    setErr(null);
    try {
      await mut.mutateAsync(justificativa.trim() || undefined);
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao aprovar Base.'));
    }
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Aprovar Base Comercial"
      description="Registra o hash do conteúdo aprovado. Qualquer alteração após a aprovação invalida a publicação (RN-COM-029, design D10)."
      submitLabel="Aprovar"
      submitVariant="success"
      isSubmitting={mut.isPending}
      submitError={err}
    >
      <Textarea
        label="Justificativa (opcional)"
        rows={3}
        value={justificativa}
        onChange={(e) => setJustificativa(e.target.value)}
      />
    </ActionModal>
  );
};

export const RejeitarBaseModal: React.FC<BaseProps> = ({ base, isOpen, onClose, onSuccess }) => {
  const [justificativa, setJustificativa] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const mut = useRejeitarBaseComercial(base.id);

  React.useEffect(() => {
    if (!isOpen) {
      setJustificativa('');
      setErr(null);
    }
  }, [isOpen]);

  const submit = async () => {
    setErr(null);
    if (justificativa.trim().length < 20) return;
    try {
      await mut.mutateAsync(justificativa.trim());
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao rejeitar Base.'));
    }
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Rejeitar Base Comercial"
      description="Devolve a Base para Em Revisão com a justificativa registrada em auditoria."
      submitLabel="Rejeitar"
      submitVariant="danger"
      isSubmitting={mut.isPending}
      submitError={err}
      canSubmit={justificativa.trim().length >= 20}
    >
      <Textarea
        label="Justificativa (mín. 20 caracteres)"
        rows={3}
        value={justificativa}
        onChange={(e) => setJustificativa(e.target.value)}
        showCharCount
        minLength={20}
      />
    </ActionModal>
  );
};

export const PublicarBaseModal: React.FC<BaseProps> = ({ base, isOpen, onClose, onSuccess }) => {
  const [err, setErr] = useState<string | null>(null);
  const mut = usePublicarBaseComercial(base.id);

  const submit = async () => {
    setErr(null);
    try {
      await mut.mutateAsync();
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao publicar Base.'));
    }
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Publicar Base Comercial"
      description="Torna a Base publicada (imutável). A publicação é bloqueada se o hash recalculado divergir do aprovado ou se já houver Base vigente para o mesmo cliente/modalidade/escopo (RN-COM-004)."
      submitLabel="Publicar"
      submitVariant="primary"
      isSubmitting={mut.isPending}
      submitError={err}
    >
      <p className="text-sm text-gray-600">
        <strong>{base.codigo} v{base.versao}</strong> — a partir da publicação a versão
        vira somente leitura; alterar condições gera nova versão (RN-COM-005).
      </p>
    </ActionModal>
  );
};

export const SuspenderBaseModal: React.FC<BaseProps> = ({ base, isOpen, onClose, onSuccess }) => {
  const [motivo, setMotivo] = useState('');
  const [confirmado, setConfirmado] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const mut = useSuspenderBaseComercial(base.id);

  // Reset ao fechar/reabrir — sem isso o estado anterior fica preso.
  React.useEffect(() => {
    if (!isOpen) {
      setMotivo('');
      setConfirmado(false);
      setErr(null);
    }
  }, [isOpen]);

  const submit = async () => {
    setErr(null);
    if (motivo.trim().length < 20 || !confirmado) return;
    try {
      await mut.mutateAsync(motivo.trim());
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao suspender Base.'));
    }
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Suspender Base Comercial"
      description="Pausa o uso da Base. Não é possível gerar novas propostas/contratos até a reativação. O motivo é registrado em auditoria."
      submitLabel="Suspender"
      submitVariant="warning"
      isSubmitting={mut.isPending}
      submitError={err}
      canSubmit={motivo.trim().length >= 20 && confirmado}
    >
      <div className="space-y-3">
        <Textarea
          label="Motivo (mín. 20 caracteres)"
          rows={3}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          showCharCount
          minLength={20}
        />
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={confirmado}
            onChange={(e) => setConfirmado(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
          />
          <span>
            Confirmo que desejo suspender <strong>{base.codigo} v{base.versao}</strong>.
          </span>
        </label>
      </div>
    </ActionModal>
  );
};

export const CancelarBaseModal: React.FC<BaseProps> = ({ base, isOpen, onClose, onSuccess }) => {
  const [motivo, setMotivo] = useState('');
  const [confirmado, setConfirmado] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const mut = useCancelarBaseComercial(base.id);

  React.useEffect(() => {
    if (!isOpen) {
      setMotivo('');
      setConfirmado(false);
      setErr(null);
    }
  }, [isOpen]);

  const submit = async () => {
    setErr(null);
    if (motivo.trim().length < 20 || !confirmado) return;
    try {
      await mut.mutateAsync(motivo.trim());
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao cancelar Base.'));
    }
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Cancelar Base Comercial"
      description="Desfecho terminal — não é reversível. Base cancelada não origina novos processos (RN-COM-024). Recomendado em vez de exclusão física (RN-COM-022)."
      submitLabel="Cancelar Base"
      submitVariant="danger"
      isSubmitting={mut.isPending}
      submitError={err}
      canSubmit={motivo.trim().length >= 20 && confirmado}
    >
      <div className="space-y-3">
        <Textarea
          label="Motivo (mín. 20 caracteres)"
          rows={3}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          showCharCount
          minLength={20}
        />
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={confirmado}
            onChange={(e) => setConfirmado(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
          />
          <span>
            Confirmo que desejo cancelar <strong>{base.codigo} v{base.versao}</strong>. Esta
            ação é irreversível.
          </span>
        </label>
      </div>
    </ActionModal>
  );
};

export const SubstituirBaseModal: React.FC<BaseProps> = ({ base, isOpen, onClose, onSuccess }) => {
  const [novaVersaoId, setNovaVersaoId] = useState('');
  const [motivo, setMotivo] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const mut = useSubstituirBaseComercial(base.id);
  const { data: versoes } = useVersoesDaBase(isOpen ? base.id : undefined);

  React.useEffect(() => {
    if (!isOpen) {
      setNovaVersaoId('');
      setMotivo('');
      setErr(null);
    }
  }, [isOpen]);

  const opcoes = useMemo(
    () =>
      (versoes?.data ?? [])
        .filter(
          (v) =>
            v.id !== base.id &&
            (v.situacao === 'publicada' || v.situacao === 'vigente'),
        )
        .map((v) => ({
          value: v.id,
          label: `${v.codigo} v${v.versao} • ${v.nome}`,
        })),
    [versoes, base.id],
  );

  const submit = async () => {
    setErr(null);
    if (!novaVersaoId || motivo.trim().length < 20) return;
    try {
      await mut.mutateAsync({ novaVersaoId, motivo: motivo.trim() });
      onSuccess?.();
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao substituir Base.'));
    }
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      title="Substituir Base Comercial"
      description="Move esta Base para Substituída e a aponta para a substituta. Contratos e OS existentes permanecem na versão original (RN-COM-026)."
      submitLabel="Substituir"
      submitVariant="primary"
      isSubmitting={mut.isPending}
      submitError={err}
      canSubmit={!!novaVersaoId && motivo.trim().length >= 20}
    >
      <div className="space-y-3">
        <AutocompleteSelect
          label="Versão substituta (publicada/vigente)"
          options={opcoes}
          value={novaVersaoId}
          onChange={setNovaVersaoId}
        />
        {opcoes.length === 0 && (
          <p className="text-xs text-gray-500">
            Nenhuma outra versão publicada/vigente no grupo para substituir. Gere uma nova
            versão antes.
          </p>
        )}
        <Textarea
          label="Motivo (mín. 20 caracteres)"
          rows={3}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          showCharCount
          minLength={20}
        />
      </div>
    </ActionModal>
  );
};
